import { add, dot, lerp, moveTowards, normalize, scale, smoothFactor, sub, v3, type Vec3 } from './math';
import type { ViewerPose } from './windowFollower';

/**
 * The hand-anchored menu placement: lazily follows the off hand and shows only
 * in the palm-up pose - port of `TrainingUxController.FollowHand`. Pure state;
 * the host feeds it poses and applies the result to the strip.
 *
 * This is the behaviour `@realitycollective/iwsdk-uiextensions` has no dock
 * mode for (windows follow the head, not a hand). Built here, structured to
 * migrate upstream as a hand-anchor mode once proven on device.
 */

export type Handedness = 'left' | 'right';

/** A tracked hand's palm, in world space. `palmNormal` points OUT of the palm surface. */
export interface PalmPose {
  readonly position: Vec3;
  readonly palmNormal: Vec3;
}

/** A controller grip, in world space. `up` is the controller's thumb-face axis; `right`/`forward` complete its frame. */
export interface GripPose {
  readonly position: Vec3;
  readonly up: Vec3;
  readonly right: Vec3;
  readonly forward: Vec3;
}

export interface HandAnchorInput {
  readonly viewer: ViewerPose;
  /** The off hand's palm when hand tracking is active; null otherwise. */
  readonly palm: PalmPose | null;
  /** The off hand's controller grip when a controller is held; null otherwise. */
  readonly grip: GripPose | null;
  readonly offHand: Handedness;
}

export interface HandAnchorOutput {
  readonly position: Vec3;
  /** 0..1 fade; the strip is interactive only while shown. */
  readonly alpha: number;
  readonly shown: boolean;
}

export interface HandAnchorOptions {
  /** ~0.25 s lazy follow from the design. */
  readonly followSeconds?: number;
  readonly fadeSeconds?: number;
  /** Palm-up gate with hysteresis: show above this dot product... */
  readonly showAboveDot?: number;
  /** ...and keep showing until it drops below this. */
  readonly hideBelowDot?: number;
}

export class HandMenuAnchor {
  private readonly followSeconds: number;
  private readonly fadeSeconds: number;
  private readonly showAboveDot: number;
  private readonly hideBelowDot: number;

  private placed = false;
  private shown = false;
  private alpha = 0;
  private position: Vec3 = v3(0, 0, 0);

  constructor(options: HandAnchorOptions = {}) {
    this.followSeconds = options.followSeconds ?? 0.25;
    this.fadeSeconds = options.fadeSeconds ?? 0.15;
    this.showAboveDot = options.showAboveDot ?? 0.55;
    this.hideBelowDot = options.hideBelowDot ?? 0.35;
  }

  get current(): HandAnchorOutput {
    return { position: this.position, alpha: this.alpha, shown: this.shown };
  }

  update(input: HandAnchorInput, deltaSeconds: number): HandAnchorOutput {
    const { viewer } = input;
    let target: Vec3;
    let wantShown: boolean;

    if (input.palm) {
      // Palm-up gate: show while the palm surface faces the head.
      const toHead = normalize(sub(viewer.position, input.palm.position));
      const facing = dot(input.palm.palmNormal, toHead);
      wantShown = facing > (this.shown ? this.hideBelowDot : this.showAboveDot);

      // Beside the palm on the ulnar (pinky) side - with the palm up, that is
      // the viewer's left for the left hand, right for the right.
      const ulnar = scale(viewer.right, input.offHand === 'left' ? -1 : 1);
      target = add(add(input.palm.position, scale(ulnar, 0.11)), v3(0, 0.02, 0));
    } else if (input.grip) {
      // Turning the palm toward the face rolls the controller's thumb face
      // (its up axis) toward the head. Show only in that pose.
      const toHead = normalize(sub(viewer.position, input.grip.position));
      const facing = dot(input.grip.up, toHead);
      wantShown = facing > (this.shown ? this.hideBelowDot : this.showAboveDot);

      // Ulnar side of the palm: a little inward and up from the grip.
      target = add(
        add(add(input.grip.position, scale(input.grip.right, 0.09)), scale(input.grip.up, 0.03)),
        scale(input.grip.forward, 0.02),
      );
    } else {
      // Nothing tracked (desktop preview) - rest at the lower left of view,
      // always shown so the menu stays testable with a mouse.
      target = add(
        add(add(viewer.position, scale(viewer.right, -0.26)), v3(0, -0.18, 0)),
        scale(viewer.forward, 0.7),
      );
      wantShown = true;
    }

    this.shown = wantShown;
    this.alpha = moveTowards(this.alpha, this.shown ? 1 : 0, deltaSeconds / this.fadeSeconds);

    if (!this.placed) {
      this.position = target;
      this.placed = true;
    } else {
      this.position = lerp(this.position, target, smoothFactor(deltaSeconds, this.followSeconds));
    }

    return this.current;
  }
}
